"use client";

import Image from "next/image";
import axios from "axios";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
    FaInstagram,
    FaFacebookF,
    FaLinkedinIn,
} from "react-icons/fa6";

const selectCls =
    "border border-gray-300 rounded-md outline-none focus:border-[var(--primary-dark)] font-[BOOWIE] w-full";
// BOOWIE's "@", "<", ">" and "~" glyphs are blank outlines, so free-typed fields
// (name/email/phone/message) use font-sans to avoid invisible characters.
const inputCls =
    "border border-gray-300 rounded-md outline-none focus:border-[var(--primary-dark)] font-sans w-full";
const inputStyle = {
    padding: "clamp(0.65rem, 1.2vw, 1rem) clamp(0.75rem, 1.5vw, 1.25rem)",
    fontSize: "clamp(0.8rem, 1.1vw, 1rem)",
};

const EMPTY_FORM = { vertical: "", name: "", email: "", phone: "", message: "" };

export default function JoinSection() {
    const [verticals, setVerticals] = useState([]);
    const [form, setForm] = useState(EMPTY_FORM);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        async function getVerticals() {
            try {
                const res = await axios.get(
                    `${process.env.NEXT_PUBLIC_BACKEND_URL}/gitartha/verticals`
                );
                setVerticals(res.data.data || []);
            } catch (error) {
                console.error("Failed to fetch verticals:", error);
            }
        }

        getVerticals();
    }, []);

    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const validate = () => {
        if (!form.vertical) return "Please select a vertical.";
        if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || !form.message.trim())
            return "All fields are required.";
        if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Please enter a valid email.";
        if (!/^[6-9]\d{9}$/.test(form.phone.replace(/[\s-]/g, "")))
            return "Please enter a valid 10-digit mobile number.";
        return null;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const err = validate();
        if (err) {
            toast.error(err);
            return;
        }

        setLoading(true);
        try {
            const res = await axios.post(`${process.env.NEXT_PUBLIC_BACKEND_URL}/contact`, {
                name: form.name.trim(),
                email: form.email.trim(),
                phone: form.phone.replace(/[\s-]/g, ""),
                message: form.message.trim(),
                vertical: form.vertical,
            });
            toast.success(res.data.message || "Thank you for contacting us.");
            setForm(EMPTY_FORM);
        } catch (error) {
            toast.error(error.response?.data?.message || "Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {/* JOIN NOW Section */}
            <div className="w-full bg-[var(--background-white)] flex flex-col items-center px-4 sm:px-8 md:px-16 py-12 md:py-16">

                <p className="text-[var(--secondary-text)] text-base sm:text-lg md:text-2xl font-light text-center font-[BOOWIE]">
                    JOIN THE GITARTHA REVOLUTION
                </p>

                <div className="flex items-center justify-center mt-6">
                    <h2 className="text-[var(--primary-dark)] text-4xl sm:text-6xl md:text-7xl font-light font-[BOOWIE] text-center">
                        JOIN NOW
                    </h2>
                </div>

                {/* Description */}
                <p className="mt-6 text-center text-sm sm:text-base md:text-lg text-[var(--secondary-text)] max-w-3xl leading-relaxed">
                    A movement where every individual, every entrepreneur, and every dreamer finds the strength to rise higher.
                    Whether you seek growth, security, or transformation, Gitartha is your partner in progress.
                </p>
            </div>


            {/* Contact & Address Section */}
            <div className="w-full bg-[var(--background-white)]"
                style={{ padding: 'clamp(2rem, 5vw, 6rem) clamp(1rem, 6vw, 8rem)' }}>

                <div style={{ maxWidth: 'min(90rem, 100%)', margin: '0 auto' }}
                    className="grid grid-cols-1 md:grid-cols-2 gap-10 xl:gap-20 items-start">

                    {/* Offices Section */}
                    <div className="flex flex-col w-full">

                        <h2
                            className="font-bold text-[var(--primary-dark)] font-[BOOWIE] text-center md:text-left mb-6 md:mb-8"
                            style={{ fontSize: 'clamp(1.5rem, 3vw, 3rem)' }}>
                            Our Offices
                        </h2>

                        <div className="grid grid-cols-1 min-[480px]:grid-cols-2 gap-4 md:gap-6">

                            {/* Registered Office */}
                            <div className="bg-white shadow-md rounded-xl border border-gray-100"
                                style={{ padding: 'clamp(1rem, 2.5vw, 1.75rem)' }}>

                                <h3
                                    className="font-semibold text-[var(--primary-dark)] mb-3"
                                    style={{ fontSize: 'clamp(1rem, 1.5vw, 1.25rem)' }}>
                                    Registered Office
                                </h3>

                                <p
                                    className="text-[var(--secondary-text)] leading-relaxed"
                                    style={{ fontSize: 'clamp(0.8rem, 1.2vw, 1rem)' }}>
                                    Flat no 1130/A, Usha Jubilee Town-36 <br />
                                    1st Floor, Road No. 36 <br />
                                    Pillor No. 1643, Jubilee Hills <br />
                                    Hyderabad, 500033
                                    <br /><br />
                                    +91 6300315391
                                    <br /><br />
                                    info@gatax.in
                                </p>

                            </div>

                            {/* Head Office */}
                            <div className="bg-white shadow-md rounded-xl border border-gray-100"
                                style={{ padding: 'clamp(1rem, 2.5vw, 1.75rem)' }}>

                                <h3
                                    className="font-semibold text-[var(--primary-dark)] mb-3"
                                    style={{ fontSize: 'clamp(1rem, 1.5vw, 1.25rem)' }}>
                                    Head Office
                                </h3>

                                <p
                                    className="text-[var(--secondary-text)] leading-relaxed"
                                    style={{ fontSize: 'clamp(0.8rem, 1.2vw, 1rem)' }}>
                                    Flat no 204, L.B Heights <br />
                                    Mahalakshmi Nagar <br />
                                    Ramachandrapuram, 533255
                                    <br /><br />
                                    +91 9030724144
                                    <br /><br />
                                    admin@gatax.in
                                </p>

                            </div>

                        </div>
                        {/* Follow Us */}
                        <div className="mt-10 flex flex-col items-center md:items-start">
                            <p className="text-[var(--primary-dark)] font-[BOOWIE] text-sm tracking-[4px] uppercase mb-5">
                                Follow Us
                            </p>

                            <div className="flex items-center gap-4">

                                <a
                                    href="https://www.instagram.com/gitarthaglobal?igsh=emMxYzd6NzdsMmVx"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-11 h-11 rounded-full border border-gray-300 flex items-center justify-center text-[#0B2239] hover:bg-[#E4405F] hover:text-white hover:border-[#E4405F] transition-all duration-300"
                                >
                                    <FaInstagram size={20} />
                                </a>

                                <a
                                    href="https://www.facebook.com/share/1EdFN1Wsxv/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-11 h-11 rounded-full border border-gray-300 flex items-center justify-center text-[#0B2239] hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2] transition-all duration-300"
                                >
                                    <FaFacebookF size={18} />
                                </a>

                                <a
                                    href="https://www.linkedin.com/company/gitartha/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-11 h-11 rounded-full border border-gray-300 flex items-center justify-center text-[#0B2239] hover:bg-[#0A66C2] hover:text-white hover:border-[#0A66C2] transition-all duration-300"
                                >
                                    <FaLinkedinIn size={18} />
                                </a>

                            </div>
                        </div>
                    </div>

                    {/* Contact Form */}
                    <form className="flex flex-col w-full"
                        style={{ gap: 'clamp(0.75rem, 1.5vw, 1.25rem)' }}
                        onSubmit={onSubmit}>

                        <select
                            name="vertical"
                            required
                            value={form.vertical}
                            onChange={onChange}
                            className={`${selectCls} bg-white text-gray-700`}
                            style={inputStyle}
                        >
                            <option value="" disabled>
                                Select Service Vertical
                            </option>

                            {verticals.map((v) => (
                                <option key={v._id} value={v._id}>
                                    {v.name}
                                </option>
                            ))}
                        </select>

                        <input
                            name="name"
                            type="text"
                            placeholder="Your Name"
                            value={form.name}
                            onChange={onChange}
                            className={inputCls}
                            style={inputStyle}
                        />
                        <input
                            name="email"
                            type="email"
                            placeholder="Your Email"
                            value={form.email}
                            onChange={onChange}
                            className={inputCls}
                            style={inputStyle}
                        />
                        <input
                            name="phone"
                            type="tel"
                            placeholder="Your Phone"
                            value={form.phone}
                            onChange={onChange}
                            className={inputCls}
                            style={inputStyle}
                        />
                        <textarea
                            name="message"
                            placeholder="Your Message"
                            rows="4"
                            value={form.message}
                            onChange={onChange}
                            className={`${inputCls} resize-none`}
                            style={inputStyle}
                        />

                        <button
                            type="submit"
                            disabled={loading}
                            className="bg-[var(--primary-dark)] text-white rounded-md hover:opacity-90 transition font-[BOOWIE] w-full disabled:opacity-60"
                            style={{
                                padding: 'clamp(0.75rem, 1.3vw, 1.1rem)',
                                fontSize: 'clamp(0.85rem, 1.1vw, 1rem)'
                            }}
                        >
                            {loading ? "Sending…" : "Submit"}
                        </button>
                    </form>

                </div>
            </div>
        </>
    );
}